import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { GraduationCap, LayoutDashboard, BookOpen, ClipboardList, Route, Grid3x3, Loader2, Plus, X, Trash2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import DatePicker from "../../components/os/DatePicker";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className={`card-surface w-full ${wide ? "max-w-xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useI18n();
  const cls = status === "completado" ? "badge-emerald" : status === "en_progreso" ? "badge-amber" : "badge-muted";
  return <span className={`badge ${cls}`}>{t(`os.learning.status.${status}`)}</span>;
}

/* ============ PANEL ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/learning/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const kpis = [
    { label: t("os.learning.assignmentsTotal"), value: d.assignments_total }, { label: t("os.learning.completed"), value: d.completed },
    { label: t("os.learning.compliance"), value: `${d.compliance}%` }, { label: t("os.learning.certificates"), value: d.certificates },
    { label: t("os.learning.pathsCount"), value: d.paths },
  ];
  return (
    <div className="space-y-8" data-testid="learning-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`learning-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="card-surface p-6">
        <div className="overline text-navy/40 mb-4">{t("os.learning.topGaps")}</div>
        {(d.top_skill_gaps || []).length === 0 ? <p className="text-sm text-navy/35">{t("os.learning.none")}</p> : d.top_skill_gaps.map((g) => (
          <div key={g.skill} className="flex items-center justify-between py-1.5 border-b border-navy/5 last:border-0">
            <span className="text-sm text-navy/70">{g.skill}</span><span className="badge badge-amber">-{g.gap}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ MI APRENDIZAJE ============ */
function MyLearningTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/learning/my").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { try { await api.patch(`/learning/assignments/${id}`, { status }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const pct = d.total ? Math.round((d.completed / d.total) * 100) : 0;
  const mpct = d.mandatory_total ? Math.round((d.mandatory_done / d.mandatory_total) * 100) : 100;
  return (
    <div className="space-y-6" data-testid="learning-my">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-surface p-5"><div className="overline text-navy/40">{t("os.learning.myProgress")}</div><div className="mt-2 text-3xl font-medium">{d.completed}/{d.total}</div><div className="mt-2 h-2 rounded-full bg-navy/5 overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${pct}%` }} /></div></div>
        <div className="card-surface p-5"><div className="overline text-navy/40">{t("os.learning.mandatoryProgress")}</div><div className="mt-2 text-3xl font-medium">{d.mandatory_done}/{d.mandatory_total}</div><div className="mt-2 h-2 rounded-full bg-navy/5 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${mpct}%` }} /></div></div>
        <div className="card-surface p-5"><div className="overline text-navy/40">{t("os.learning.certificates")}</div><div className="mt-2 text-3xl font-medium">{d.certificates}</div></div>
      </div>
      {d.assignments.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.learning.noAssignments")}</div>
        : (
          <div className="space-y-2" data-testid="learning-my-list">
            {d.assignments.map((a) => (
              <div key={a.id} className="card-surface p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`learning-my-${a.id}`}>
                <div><div className="font-medium flex items-center gap-2">{a.course_title}{a.mandatory && <span className="badge badge-danger">{t("os.learning.mandatory")}</span>}</div>{a.due_date && <div className="text-xs text-navy/50">{t("os.learning.dueDate")}: {a.due_date.slice(0, 10)}</div>}</div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={a.status} />
                  {a.status !== "completado" && <>
                    {a.status === "asignado" && <button onClick={() => setStatus(a.id, "en_progreso")} className="btn btn-ghost text-sm" data-testid={`learning-my-progress-${a.id}`}>{t("os.learning.markProgress")}</button>}
                    <a href="/os/academia" className="btn btn-outline text-sm"><ExternalLink className="w-3.5 h-3.5" /> {t("os.learning.goToAcademy")}</a>
                  </>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ============ ASIGNACIONES (leadership) ============ */
function AssignmentsTab({ meta }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", course_id: "", mandatory: false, due_date: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/learning/assignments", { params: { scope: "all" } }); setItems(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!f.user_id || !f.course_id) return toast.error(t("os.learning.course"));
    setSaving(true);
    try { await api.post("/learning/assignments", { ...f, due_date: f.due_date || null }); toast.success(t("os.learning.assign")); setOpen(false); setF({ user_id: "", course_id: "", mandatory: false, due_date: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/learning/assignments/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="learning-assignments">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="learning-new-assignment-btn"><Plus className="w-4 h-4" /> {t("os.learning.newAssignment")}</button></div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : items.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.learning.noAssignments")}</div>
        : (
          <div className="space-y-2" data-testid="learning-assignments-list">
            {items.map((a) => (
              <div key={a.id} className="card-surface p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`learning-assignment-${a.id}`}>
                <div><div className="font-medium flex items-center gap-2">{a.course_title}{a.mandatory && <span className="badge badge-danger">{t("os.learning.mandatory")}</span>}</div><div className="text-xs text-navy/50">{a.user_name}{a.due_date ? ` · ${a.due_date.slice(0, 10)}` : ""}</div></div>
                <div className="flex items-center gap-2"><StatusBadge status={a.status} /><button onClick={() => del(a.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.learning.newAssignment")} onClose={() => setOpen(false)} testid="learning-assignment-dialog">
          <div className="space-y-3">
            <select className="field" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="learning-assign-user">
              <option value="">{t("os.learning.employee")}</option>
              {(meta.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select className="field" value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })} data-testid="learning-assign-course">
              <option value="">{t("os.learning.course")}</option>
              {(meta.courses || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <DatePicker testid="learn-due-date" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} placeholder="Fecha límite" />
            <label className="flex items-center gap-2 text-sm text-navy/60"><input type="checkbox" checked={f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} /> {t("os.learning.mandatory")}</label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.learning.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="learning-assign-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.learning.assign")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ RUTAS ============ */
function PathsTab({ meta }) {
  const { t } = useI18n();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [assign, setAssign] = useState(null);
  const [f, setF] = useState({ title: "", description: "", course_ids: [] });
  const [assignUser, setAssignUser] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/learning/paths"); setPaths(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggleCourse = (cid) => setF({ ...f, course_ids: f.course_ids.includes(cid) ? f.course_ids.filter((x) => x !== cid) : [...f.course_ids, cid] });
  const submit = async () => {
    if (!f.title.trim() || f.course_ids.length === 0) return toast.error(t("os.learning.selectCourses"));
    setSaving(true);
    try { await api.post("/learning/paths", f); toast.success(t("os.learning.pathCreated")); setOpen(false); setF({ title: "", description: "", course_ids: [] }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const doAssign = async () => {
    if (!assignUser) return toast.error(t("os.learning.employee"));
    try { const r = await api.post(`/learning/paths/${assign.id}/assign`, { user_id: assignUser }); toast.success(`${r.data.assigned} ${t("os.learning.assignedCourses")}`); setAssign(null); setAssignUser(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { try { await api.delete(`/learning/paths/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="learning-paths">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="learning-new-path-btn"><Plus className="w-4 h-4" /> {t("os.learning.newPath")}</button></div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : paths.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.learning.noPaths")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="learning-paths-list">
            {paths.map((p) => (
              <div key={p.id} className="card-surface p-4" data-testid={`learning-path-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-medium">{p.title}</div><div className="text-xs text-navy/50">{p.courses.length} {t("os.learning.courses")}</div></div>
                  <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {p.description && <p className="text-sm text-navy/55 mt-1">{p.description}</p>}
                <ul className="mt-2 space-y-1">{p.courses.map((c) => <li key={c.id} className="text-xs text-navy/50">• {c.title}</li>)}</ul>
                <button onClick={() => setAssign(p)} className="btn btn-outline text-sm mt-3" data-testid={`learning-assign-path-${p.id}`}>{t("os.learning.assignPath")}</button>
              </div>
            ))}
          </div>
        )}
      {open && (
        <Modal title={t("os.learning.newPath")} onClose={() => setOpen(false)} testid="learning-path-dialog" wide>
          <div className="space-y-3">
            <input className="field" placeholder={t("os.learning.pathTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="learning-path-title" />
            <textarea className="field" rows={2} placeholder={t("os.learning.description")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            <div>
              <div className="text-sm font-medium mb-1.5">{t("os.learning.selectCourses")}</div>
              <div className="max-h-48 overflow-y-auto space-y-1 border border-navy/10 rounded-lg p-2">
                {(meta.courses || []).length === 0 ? <p className="text-xs text-navy/40 p-2">{t("os.learning.none")}</p> : meta.courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-navy/70 py-1 cursor-pointer">
                    <input type="checkbox" checked={f.course_ids.includes(c.id)} onChange={() => toggleCourse(c.id)} /> {c.title}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.learning.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="learning-path-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.learning.create")}</button>
          </div>
        </Modal>
      )}
      {assign && (
        <Modal title={t("os.learning.assignPath")} onClose={() => setAssign(null)} testid="learning-assign-path-dialog">
          <select className="field" value={assignUser} onChange={(e) => setAssignUser(e.target.value)} data-testid="learning-assign-path-user">
            <option value="">{t("os.learning.employee")}</option>
            {(meta.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setAssign(null)} className="btn btn-ghost">{t("os.learning.cancel")}</button>
            <button onClick={doAssign} className="btn btn-primary" data-testid="learning-assign-path-confirm">{t("os.learning.assign")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ HABILIDADES ============ */
function SkillsTab({ meta, isLeader, myId }) {
  const { t } = useI18n();
  const [targetUser, setTargetUser] = useState(myId);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ skill: "", current_level: 0, target_level: 3 });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async (uid) => {
    setLoading(true);
    try { const r = await api.get("/learning/skills", { params: isLeader && uid ? { user_id: uid } : {} }); setSkills(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, [isLeader]);
  useEffect(() => { load(targetUser); }, [targetUser, load]);
  const submit = async () => {
    if (!f.skill.trim()) return toast.error(t("os.learning.skill"));
    setSaving(true);
    try { await api.put("/learning/skills", { user_id: isLeader ? targetUser : myId, skill: f.skill, current_level: Number(f.current_level), target_level: Number(f.target_level) }); toast.success(t("os.learning.skillSaved")); setOpen(false); setF({ skill: "", current_level: 0, target_level: 3 }); load(targetUser); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const setLevel = async (s, field, val) => {
    try { await api.put("/learning/skills", { user_id: s.user_id, skill: s.skill, current_level: field === "current_level" ? Number(val) : s.current_level, target_level: field === "target_level" ? Number(val) : s.target_level }); load(targetUser); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-5" data-testid="learning-skills">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isLeader && (
          <select className="field max-w-xs" value={targetUser} onChange={(e) => setTargetUser(e.target.value)} data-testid="learning-skills-user">
            <option value={myId}>{t("os.learning.viewFor")}: yo</option>
            {(meta.users || []).filter((u) => u.id !== myId).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <button onClick={() => setOpen(true)} className="btn btn-primary ml-auto" data-testid="learning-new-skill-btn"><Plus className="w-4 h-4" /> {t("os.learning.newSkill")}</button>
      </div>
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : skills.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.learning.noSkills")}</div>
        : (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm" data-testid="learning-skills-table">
              <thead><tr className="text-left text-navy/40 border-b border-navy/10">
                <th className="p-3 font-medium">{t("os.learning.skill")}</th><th className="p-3 font-medium">{t("os.learning.current")}</th>
                <th className="p-3 font-medium">{t("os.learning.target")}</th><th className="p-3 font-medium">{t("os.learning.gap")}</th>
              </tr></thead>
              <tbody>
                {skills.map((s) => (
                  <tr key={s.skill} className="border-b border-navy/5" data-testid={`learning-skill-${s.skill}`}>
                    <td className="p-3 font-medium">{s.skill}</td>
                    <td className="p-3">
                      <select className="field-sm w-16" value={s.current_level} onChange={(e) => setLevel(s, "current_level", e.target.value)} data-testid={`learning-skill-current-${s.skill}`}>
                        {meta.skill_levels.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>
                    <td className="p-3">
                      {isLeader ? (
                        <select className="field-sm w-16" value={s.target_level} onChange={(e) => setLevel(s, "target_level", e.target.value)}>
                          {meta.skill_levels.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      ) : <span className="text-navy/70">{s.target_level}</span>}
                    </td>
                    <td className="p-3">{s.gap > 0 ? <span className="badge badge-amber">-{s.gap}</span> : <Check className="w-4 h-4 text-emerald-500" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {open && (
        <Modal title={t("os.learning.newSkill")} onClose={() => setOpen(false)} testid="learning-skill-dialog">
          <div className="space-y-3">
            <input className="field" placeholder={t("os.learning.skill")} value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} data-testid="learning-skill-name" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-navy/45">{t("os.learning.current")}</label><select className="field" value={f.current_level} onChange={(e) => setF({ ...f, current_level: e.target.value })}>{meta.skill_levels.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div><label className="text-xs text-navy/45">{t("os.learning.target")}</label><select className="field" value={f.target_level} onChange={(e) => setF({ ...f, target_level: e.target.value })}>{meta.skill_levels.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setOpen(false)} className="btn btn-ghost">{t("os.learning.cancel")}</button>
            <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="learning-skill-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.learning.save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============ STUDIO ============ */
export default function LearningStudio({ embedded = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(isLeader ? "dashboard" : "mylearning");
  useEffect(() => { api.get("/learning/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const tabs = [
    ...(isLeader ? [{ key: "dashboard", label: t("os.learning.tabs.dashboard"), icon: LayoutDashboard }] : []),
    { key: "mylearning", label: t("os.learning.tabs.mylearning"), icon: BookOpen },
    ...(isLeader ? [
      { key: "assignments", label: t("os.learning.tabs.assignments"), icon: ClipboardList },
      { key: "paths", label: t("os.learning.tabs.paths"), icon: Route },
    ] : []),
    { key: "skills", label: t("os.learning.tabs.skills"), icon: Grid3x3 },
  ];

  const Shell = ({ children }) => embedded ? <>{children}</> : <OSLayout title={t("os.learning.title")}>{children}</OSLayout>;

  return (
    <Shell>
      <div className="space-y-8" data-testid="learning-page">
        {!embedded && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5" /> {t("os.learning.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.learning.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.learning.subtitle")}</p>
        </motion.div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`learning-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "dashboard" && isLeader && <DashboardTab />}
            {tab === "mylearning" && <MyLearningTab />}
            {tab === "assignments" && isLeader && <AssignmentsTab meta={meta} />}
            {tab === "paths" && isLeader && <PathsTab meta={meta} />}
            {tab === "skills" && <SkillsTab meta={meta} isLeader={isLeader} myId={user?.id} />}
          </>
        )}
      </div>
    </Shell>
  );
}
